import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from './TalentsPage.module.css';
import { applySkillOrder } from '../../shared/utils/skillOrder';
import PageLayout from '../../components/layout/PageLayout/PageLayout';
import FadeImage from '../../shared/components/FadeImage';
import { imageProtectionProps } from '../../shared/utils/imageProtection';
import LoadingSpinner from '../../shared/components/LoadingSpinner';
import { C, ff, t } from '../Talent/Dashboard/components/dashboardConstants';
import { getTalentList, searchTalentsByAi } from '../../api';
import { useAuth } from '../../contexts/AuthContext';
import { useSavedItems } from '../../contexts/SavedItemsContext';
import RoleOnlyModal from '../../shared/components/RoleOnlyModal';
import BoardPickerModal, { SavedConfirmModal } from '../../shared/components/BoardPickerModal';
import GroupedPagination from '../../components/common/Pagination/GroupedPagination';
import { AdGridCard } from '../../shared/components/AdBanner';
import { useRotatingAd } from '../../shared/hooks/useAds';
import { useAiSearchAvailability } from './useAiSearchAvailability';
import { pickAiPlaceholder } from './aiPlaceholders';
import { AI_SEARCH_MAX_QUERY, conditionRows, describeAiSearchError } from './AiSearchParts';

// ── API 응답 → 컴포넌트 모델 변환 ─────────────────────────────────────────────
const GENDER_MAP = { 
  MALE: '남성', FEMALE: '여성', 
  male: '남성', female: '여성',
  '남성': '남성', '여성': '여성'
};

function getTalentProfileId(item) {
  return (
    item?.talentProfileId ??
    item?.id ??
    item?.talentProfile?.id ??
    item?.talentProfile?.talentProfileId ??
    item?.talent?.id ??
    item?.talent?.talentProfileId ??
    null
  );
}

export function mapTalent(item) {
  const medias = item.medias || [];
  const photoUrl = item.profileImageUrl
    ?? medias.find(m => m.photoType === 'PROFILE_CLOSE_UP')?.fileUrl
    ?? null;

  // 나이 계산
  const birthYear = item.birthDate ? new Date(item.birthDate).getFullYear() : null;
  const currentYear = new Date().getFullYear();
  const age = birthYear !== null ? currentYear - birthYear : null;

  return {
    id: getTalentProfileId(item),
    slug: item.slug || String(getTalentProfileId(item)),
    name: item.stageName || item.name, // 예명이 있으면 예명 노출
    realName: item.name,
    photoUrl,
    verified: false,
    gender: GENDER_MAP[item.gender] || item.gender || '-',
    age: age !== null ? `${age}세` : '-',
    ageNum: age,
    height: item.heightCm ? `${item.heightCm}cm` : '-',
    weight: item.weightKg ? `${item.weightKg}kg` : '-',
    weightNum: item.weightKg ?? null,
    tags: applySkillOrder(getTalentProfileId(item), (item.skills || []).map(s => s.name || s.skillName || s).filter(Boolean)),
    specialty: '-',
    saved: false,
    archiveItemId: null,
  };
}

const PAGE_SIZE = 15;

/** AI 검색 버튼의 Queen 심볼 */
function AiOrbMark() {
  return (
    <img
      src="/assets/images/Queen-14.png"
      alt=""
      width={28}
      height={28}
      draggable={false}
    />
  );
}

export const withPhotoOnly = (list, on) => (
  on ? list.filter(t => typeof t.photoUrl === 'string' && t.photoUrl.trim()) : list
);

/** AI 결과도 목록과 같이 15명씩 나눠 본다. 결과가 없어도 페이지는 1쪽이다. */
export const aiPageCount = (list) => Math.max(1, Math.ceil(list.length / PAGE_SIZE));
export const aiPageOf = (list, page) => list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

const SKILL_CATEGORIES = [
  {
    id: 'language', label: '언어 / 사투리',
    groups: [
      { name: '외국어', items: ['영어(회화)', '영어(비즈니스)', '영어(네이티브)', '일본어', '중국어(표준)', '중국어(광둥)', '프랑스어', '스페인어', '독일어'] },
      { name: '사투리', items: ['서울/표준어', '경상도', '전라도', '충청도', '강원도', '제주도', '북한'] },
    ],
  },
  {
    id: 'action', label: '액션 / 무술',
    groups: [{ name: '', items: ['태권도', '유도', '검도', '복싱', '무에타이', '킥복싱', '합기도', '공수도', '쿵푸', '스턴트', '격투기'] }],
  },
  {
    id: 'vocal', label: '노래 / 보컬',
    groups: [{ name: '', items: ['팝', '발라드', '뮤지컬', '재즈', '힙합/R&B', '클래식(성악)', '록/메탈', '트로트'] }],
  },
  {
    id: 'instrument', label: '악기',
    groups: [{ name: '', items: ['피아노', '기타', '베이스기타', '드럼', '바이올린', '첼로', '플루트', '색소폰', '트럼펫', '해금', '가야금'] }],
  },
  {
    id: 'dance', label: '춤 / 퍼포먼스',
    groups: [{ name: '', items: ['K-팝', '힙합', '브레이크댄스', '발레', '재즈댄스', '탭댄스', '한국무용', '현대무용', '라틴댄스', '폴댄스'] }],
  },
  {
    id: 'sports', label: '스포츠 / 신체활동',
    groups: [{ name: '', items: ['수영', '승마', '스케이트보드', '스키/스노보드', '서핑', '다이빙', '체조', '펜싱', '골프', '테니스', '탁구', '농구', '축구', '야구'] }],
  },
];

function SaveIcon({ saved }) {
  if (saved) {
    return (
      <svg width="18" height="20" viewBox="0 0 18 20" fill="currentColor" aria-hidden="true">
        <path d="M3 1h12a2 2 0 0 1 2 2v15.5l-8-4.5-8 4.5V3a2 2 0 0 1 2-2z"/>
      </svg>
    );
  }
  return (
    <svg width="18" height="20" viewBox="0 0 18 20" fill="none" aria-hidden="true">
      <path d="M3 1h12a2 2 0 0 1 2 2v15.5l-8-4.5-8 4.5V3a2 2 0 0 1 2-2z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

export function TalentCard({ talent, onDetail, onSave }) {
  const [btnHovered, setBtnHovered] = useState(false);
  const [cardHovered, setCardHovered] = useState(false);
  const [scrollDist, setScrollDist] = useState(0);
  const wrapperRef = useRef(null);
  const innerRef = useRef(null);
  const iconColor = talent.saved ? '#ffb517' : (btnHovered ? '#090909' : '#888896');

  useEffect(() => {
    const wrapper = wrapperRef.current;
    const inner = innerRef.current;
    if (!wrapper || !inner) return;

    const calc = () => {
      const dist = inner.scrollWidth - wrapper.offsetWidth;
      setScrollDist(dist > 10 ? dist : 0);
    };

    calc();
    const ro = new ResizeObserver(calc);
    ro.observe(wrapper);
    ro.observe(inner);
    return () => ro.disconnect();
  }, [talent.tags]);

  const canAnimate = scrollDist > 0;
  const duration = canAnimate ? Math.max(scrollDist / 40, 1) : 0;

  return (
    <div
      onClick={() => onDetail(talent.id)}
      style={{ background: C.white, borderRadius: '16px', overflow: 'hidden', border: `1px solid ${C.g200}`, cursor: 'pointer', transition: 'all 0.2s ease', boxShadow: '0 4px 15px rgba(0,0,0,0.06)' }}
      onMouseEnter={e => { setCardHovered(true); e.currentTarget.style.borderColor = '#090909'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)'; }}
      onMouseLeave={e => { setCardHovered(false); e.currentTarget.style.borderColor = C.g200; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.06)'; }}
    >
      {/* Photo */}
      <div style={{ position: 'relative', width: '100%', aspectRatio: '3/4', overflow: 'hidden' }}>
        <FadeImage
          src={talent.photoUrl}
          alt={talent.name}
          size="medium"
          placeholder="talent"
          width={220}
          height={293}
          {...imageProtectionProps}
        />
        <button
          onClick={e => { e.stopPropagation(); onSave(talent.id); }}
          onMouseEnter={() => setBtnHovered(true)}
          onMouseLeave={() => setBtnHovered(false)}
          style={{ position: 'absolute', top: '10px', right: '10px', width: '32px', height: '32px', borderRadius: '8px', background: talent.saved ? '#fff7e8' : 'rgba(255,255,255,0.9)', border: talent.saved ? '1px solid #ffd384' : 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: iconColor, transition: 'color 0.15s, background 0.15s, border-color 0.15s' }}
          aria-label={talent.saved ? '저장 해제' : '저장하기'}
        >
          <SaveIcon saved={talent.saved} />
        </button>
        <div
          ref={wrapperRef}
          className={styles.tagOverlay}
        >
          <div
            ref={innerRef}
            className={`${styles.tagInner}${cardHovered && canAnimate ? ` ${styles.tagInnerAnimating}` : ''}`}
            style={canAnimate ? { '--sd': `${scrollDist}px`, '--dur': `${duration}s` } : {}}
          >
            {talent.tags.map(tag => (
              <span key={tag} style={{ padding: '3px 8px', borderRadius: '6px', background: 'rgba(0,0,0,0.55)', whiteSpace: 'nowrap', flexShrink: 0, ...t('11px', 500, C.white) }}>{tag}</span>
            ))}
          </div>
        </div>
      </div>
      {/* Info */}
      <div style={{ padding: '12px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '4px' }}>
          <span style={t('16px', 700, C.bk)}>{talent.name}</span>
          {talent.verified && (
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" fill={C.blue} stroke={C.blue} strokeWidth="0" /><path d="M9 12l2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0', flexWrap: 'wrap' }}>
          <span style={t('12px', 400, C.sub)}>{talent.gender}</span>
          <span style={{ margin: '0 6px', color: C.g300 }}>|</span>
          <span style={t('12px', 400, C.sub)}>{talent.age}</span>
          <span style={{ margin: '0 6px', color: C.g300 }}>|</span>
          <span style={t('12px', 400, C.sub)}>{talent.height}</span>
        </div>
      </div>
    </div>
  );
}

// toSlider: 실제값 → 슬라이더 내부값(0~100), fromSlider: 역변환
// 기본은 선형. 키처럼 비선형이 필요한 경우 외부에서 주입.
const LINEAR_SCALE = {
  to: (v, min, max) => ((v - min) / (max - min)) * 100,
  from: (s, min, max) => Math.round(min + (s / 100) * (max - min)),
};

// 키 전용: 100~150 → 슬라이더 0~50%, 150~220 → 슬라이더 50~100%
const HEIGHT_SCALE = {
  to: (v) => {
    if (v <= 150) return ((v - 100) / 50) * 50;
    return 50 + ((v - 150) / 70) * 50;
  },
  from: (s) => {
    if (s <= 50) return Math.round(100 + (s / 50) * 50);
    return Math.round(150 + ((s - 50) / 50) * 70);
  },
};

function RangeSlider({ label, minVal, maxVal, min, max, unit, onChangeMin, onChangeMax, scale }) {
  const sc = scale || LINEAR_SCALE;
  const sMin = sc.to(minVal, min, max);
  const sMax = sc.to(maxVal, min, max);
  const trackBg = `linear-gradient(to right, #e0e0e0 ${sMin}%, ${C.point} ${sMin}%, ${C.point} ${sMax}%, #e0e0e0 ${sMax}%)`;
  const isDefault = minVal === min && maxVal === max;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <span style={t('15px', 600, C.bk)}>{label}</span>
        <span style={{ ...t('13px', 700, isDefault ? C.g400 : C.point), background: isDefault ? '#f5f5f5' : '#fff5f0', padding: '3px 10px', borderRadius: '8px' }}>
          {minVal}{unit} ~ {maxVal}{unit}
        </span>
      </div>
      <div style={{ position: 'relative', height: '4px', margin: '16px 0 28px' }}>
        <div style={{ position: 'absolute', inset: 0, borderRadius: '2px', background: trackBg }} />
        {/* 중간 눈금선 (키 전용) */}
        {scale && (
          <div style={{ position: 'absolute', left: '50%', top: '-4px', width: '1px', height: '12px', background: C.g300, transform: 'translateX(-50%)', pointerEvents: 'none' }} />
        )}
        <input type="range" min={0} max={100} step={0.1} value={sMin}
          onChange={e => {
            const real = sc.from(Number(e.target.value), min, max);
            if (real <= maxVal) onChangeMin(real);
          }}
          className={styles.rangeInput}
          style={{ position: 'absolute', inset: 0, background: 'transparent', zIndex: sMin > 95 ? 5 : 4 }}
        />
        <input type="range" min={0} max={100} step={0.1} value={sMax}
          onChange={e => {
            const real = sc.from(Number(e.target.value), min, max);
            if (real >= minVal) onChangeMax(real);
          }}
          className={styles.rangeInput}
          style={{ position: 'absolute', inset: 0, background: 'transparent', zIndex: 5 }}
        />
        {minVal !== min && (
          <div style={{ position: 'absolute', top: '-26px', left: `calc(${sMin}% + ${8 - sMin * 0.16}px)`, transform: 'translateX(-50%)', background: C.point, color: C.white, fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '5px', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
            {minVal}{unit}
          </div>
        )}
        {maxVal !== max && (
          <div style={{ position: 'absolute', top: '-26px', left: `calc(${sMax}% + ${8 - sMax * 0.16}px)`, transform: 'translateX(-50%)', background: C.point, color: C.white, fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '5px', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
            {maxVal}{unit}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span style={t('11px', 400, C.g400)}>{min}{unit}</span>
        {scale && <span style={t('11px', 400, C.g400)}>150{unit}</span>}
        <span style={t('11px', 400, C.g400)}>{max}{unit}</span>
      </div>
    </div>
  );
}

function ChevronIcon({ up }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ transform: up ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
      <path d="M6 9l6 6 6-6" stroke={C.g400} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SkillCategoryFilter({ category, selected, onToggle }) {
  const [open, setOpen] = useState(false);
  const selectedCount = category.groups.reduce((cnt, g) => cnt + g.items.filter(item => selected.includes(item)).length, 0);
  return (
    <div style={{ borderRadius: '10px', border: `1px solid ${selectedCount > 0 ? C.bk : C.g200}`, overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(p => !p)}
        style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', background: open ? '#f8f8f8' : C.white, border: 'none', cursor: 'pointer' }}
      >
        <span style={t('14px', 600, C.bk)}>
          {category.label}
          {selectedCount > 0 && <span style={{ marginLeft: '6px', ...t('12px', 700, C.point) }}>{selectedCount}</span>}
        </span>
        <ChevronIcon up={open} />
      </button>
      {open && (
        <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: '12px', borderTop: `1px solid ${C.g200}` }}>
          {category.groups.map((group, gi) => (
            <div key={gi}>
              {group.name && <div style={{ ...t('12px', 600, C.sub), marginBottom: '8px' }}>{group.name}</div>}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {group.items.map(item => {
                  const active = selected.includes(item);
                  return (
                    <button key={item} onClick={() => onToggle(item)}
                      style={{ padding: '6px 12px', borderRadius: '100px', border: `1px solid ${active ? C.bk : C.g200}`, background: active ? C.bk : C.white, cursor: 'pointer', ...t('13px', 500, active ? C.white : C.bk), transition: 'all 0.15s' }}>
                      {item}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ALL_SKILL_ITEMS = SKILL_CATEGORIES.flatMap(cat => cat.groups.flatMap(g => g.items));

function FilterSidebar({ filters, sort, onApply, onReset, onClose, getFilteredCount }) {
  const [tempFilters, setTempFilters] = useState(filters);
  const [tempSort, setTempSort] = useState(sort || 'latest');
  const [skillSearch, setSkillSearch] = useState('');
  const [liveCount, setLiveCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(() => {
      getFilteredCount(tempFilters, tempSort).then((count) => {
        if (!cancelled) setLiveCount(count);
      });
    }, 180);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [tempFilters, tempSort, getFilteredCount]);

  const handleTempChange = (key, value) => {
    if (key === 'tags') {
      if (Array.isArray(value)) {
        setTempFilters(prev => ({ ...prev, tags: [] }));
      } else {
        setTempFilters(prev => {
          const cur = prev.tags;
          const next = cur.includes(value) ? cur.filter(t => t !== value) : [...cur, value];
          return { ...prev, tags: next };
        });
      }
    } else {
      setTempFilters(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleApply = () => {
    onApply(tempFilters, tempSort);
    onClose();
  };

  const handleReset = () => {
    const empty = { gender: '전체', ageMin: '', ageMax: '', heightMin: '', heightMax: '', weightMin: '', weightMax: '', tags: [] };
    setTempFilters(empty);
    setTempSort('latest');
    onReset();
  };
  const searchResults = skillSearch.trim()
    ? ALL_SKILL_ITEMS.filter(item => item.toLowerCase().includes(skillSearch.trim().toLowerCase()))
    : [];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex' }} onClick={onClose}>
      <div style={{ flex: 1 }} />
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: '360px', background: C.white, height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)' }}
      >
        {/* 상단 고정: 헤더 + 버튼 */}
        <div style={{ padding: '40px 32px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <span style={t('22px', 700, C.bk)}>필터</span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke={C.g400} strokeWidth="2" strokeLinecap="round" /></svg>
            </button>
          </div>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
            <button onClick={handleReset} style={{ flex: 1, padding: '12px', borderRadius: '12px', border: `1px solid ${C.g200}`, background: C.white, cursor: 'pointer', ...t('15px', 600, C.bk) }}>
              초기화
            </button>
            <button onClick={handleApply} style={{ flex: 2, padding: '12px', borderRadius: '12px', border: 'none', background: C.bk, cursor: 'pointer', ...t('15px', 600, C.white) }}>
              적용 {liveCount !== null ? `(${liveCount}명)` : ''}
            </button>
          </div>
        </div>

        {/* 스크롤 가능한 필터 영역 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 32px 40px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
          {/* Gender */}
          <div>
            <div style={{ ...t('15px', 600, C.bk), marginBottom: '12px' }}>성별</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {['전체', '남성', '여성'].map(g => (
                <button key={g} onClick={() => handleTempChange('gender', g)}
                  style={{ flex: 1, padding: '10px', borderRadius: '10px', border: `1px solid ${tempFilters.gender === g ? C.bk : C.g200}`, background: tempFilters.gender === g ? C.bk : C.white, cursor: 'pointer', ...t('14px', 500, tempFilters.gender === g ? C.white : C.bk) }}>
                  {g}
                </button>
              ))}
            </div>
          </div>

          {/* Age */}
          <RangeSlider
            label="나이"
            minVal={tempFilters.ageMin !== '' ? Number(tempFilters.ageMin) : 0}
            maxVal={tempFilters.ageMax !== '' ? Number(tempFilters.ageMax) : 80}
            min={0} max={80} unit="세"
            onChangeMin={v => handleTempChange('ageMin', v === 0 ? '' : v)}
            onChangeMax={v => handleTempChange('ageMax', v === 80 ? '' : v)}
          />

          {/* Height */}
          <RangeSlider
            label="키"
            minVal={tempFilters.heightMin !== '' ? Number(tempFilters.heightMin) : 100}
            maxVal={tempFilters.heightMax !== '' ? Number(tempFilters.heightMax) : 220}
            min={100} max={220} unit="cm"
            scale={HEIGHT_SCALE}
            onChangeMin={v => handleTempChange('heightMin', v === 100 ? '' : v)}
            onChangeMax={v => handleTempChange('heightMax', v === 220 ? '' : v)}
          />

          {/* Weight */}
          <RangeSlider
            label="몸무게"
            minVal={tempFilters.weightMin !== '' ? Number(tempFilters.weightMin) : 0}
            maxVal={tempFilters.weightMax !== '' ? Number(tempFilters.weightMax) : 150}
            min={0} max={150} unit="kg"
            onChangeMin={v => handleTempChange('weightMin', v === 0 ? '' : v)}
            onChangeMax={v => handleTempChange('weightMax', v === 150 ? '' : v)}
          />

          {/* Specialty Tags (다중선택 - 상위/하위 카테고리) */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <span style={t('15px', 600, C.bk)}>특기</span>
              {tempFilters.tags.length > 0 && (
                <button onClick={() => handleTempChange('tags', [])} style={{ background: 'none', border: 'none', cursor: 'pointer', ...t('13px', 500, C.sub) }}>
                  초기화
                </button>
              )}
            </div>

            {/* 선택된 특기 태그 */}
            {tempFilters.tags.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                {tempFilters.tags.map(tag => (
                  <span key={tag} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '5px 10px', borderRadius: '100px', background: C.bk, ...t('13px', 500, C.white) }}>
                    {tag}
                    <button onClick={() => handleTempChange('tags', tag)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke={C.white} strokeWidth="2" strokeLinecap="round" /></svg>
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* 특기 검색 */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{ position: 'relative' }}>
                <svg style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle cx="11" cy="11" r="8" stroke={C.g300} strokeWidth="1.8" />
                  <path d="M21 21l-4.35-4.35" stroke={C.g300} strokeWidth="1.8" strokeLinecap="round" />
                </svg>
                <input
                  value={skillSearch}
                  onChange={e => setSkillSearch(e.target.value)}
                  placeholder="특기 검색"
                  style={{ width: '100%', padding: '10px 12px 10px 36px', border: `1px solid ${C.g200}`, borderRadius: '10px', fontFamily: ff, fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              {skillSearch.trim() && (
                <div style={{ marginTop: '8px', border: `1px solid ${C.g200}`, borderRadius: '10px', maxHeight: '160px', overflowY: 'auto' }}>
                  {searchResults.length > 0 ? (
                    searchResults.map(item => {
                      const active = tempFilters.tags.includes(item);
                      return (
                        <button key={item} onClick={() => { handleTempChange('tags', item); setSkillSearch(''); }}
                          style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: active ? '#f5f5f5' : C.white, border: 'none', borderBottom: `1px solid ${C.g200}`, cursor: 'pointer', ...t('13px', 400, C.bk) }}>
                          <span>{item}</span>
                          {active
                            ? <span style={t('12px', 500, C.sub)}>선택됨</span>
                            : <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke={C.bk} strokeWidth="2" strokeLinecap="round" /></svg>
                          }
                        </button>
                      );
                    })
                  ) : (
                    <div style={{ padding: '14px', textAlign: 'center', ...t('13px', 400, C.sub) }}>
                      "{skillSearch.trim()}" 에 해당하는 특기가 없습니다
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* 카테고리 아코디언 */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {SKILL_CATEGORIES.map(cat => (
                <SkillCategoryFilter
                  key={cat.id}
                  category={cat}
                  selected={tempFilters.tags}
                  onToggle={item => handleTempChange('tags', item)}
                />
              ))}
            </div>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}



function readJson(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

const EMPTY_FILTERS = { gender: '전체', ageMin: '', ageMax: '', heightMin: '', heightMax: '', weightMin: '', weightMax: '', tags: [] };


/** 응답 모양이 배열이든 content/list든 목록만 꺼낸다 */
const listOf = (res) => (Array.isArray(res) ? res : (res?.content ?? res?.list ?? []));

/**
 * 광고가 그리드 한 칸을 차지하는 페이지는 배우를 한 명 덜 싣는다.
 * 그래야 한 페이지에 보이는 칸 수가 늘 PAGE_SIZE로 같다.
 */
export const talentsOnPage = (page, adShown) => PAGE_SIZE - (page === 1 && adShown ? 1 : 0);

/** 그 페이지의 첫 배우가 전체 목록에서 몇 번째인지 (0부터) */
export const talentOffset = (page, adShown) => (
  page === 1 ? 0 : talentsOnPage(1, adShown) + (page - 2) * PAGE_SIZE
);

/** 광고가 밀어낸 배우까지 담으려면 마지막 페이지가 하나 더 필요할 수 있다 */
/**
 * 사진 있는 배우만 볼 때의 전체 인원 어림수.
 *
 * 서버가 사진 유무로 걸러주지 못해, 지금까지 받아본 만큼의 비율로 미뤄 짐작한다.
 * 끝까지 받아봤다면(exhausted) 어림이 아니라 정확한 수다.
 *
 * @param {number} seenRaw  지금까지 받아본 사람 수
 * @param {number} seenKept 그중 사진이 있는 사람 수
 * @param {number} total    서버가 말한 전체 인원
 * @param {boolean} exhausted 끝까지 받아봤는가
 */
export const estimateKeptTotal = (seenRaw, seenKept, total, exhausted) => {
  if (exhausted) return seenKept;
  if (seenRaw <= 0) return total;
  return Math.max(seenKept, Math.round(total * (seenKept / seenRaw)));
};

export const pageCountOf = (total, adShown) => {
  const first = talentsOnPage(1, adShown);
  return total <= first ? 1 : 1 + Math.ceil((total - first) / PAGE_SIZE);
};

function buildParams(page, search, filters, sort) {
  const params = {
    callpage: page,
    perpage: PAGE_SIZE,
    orderBy: sort === 'oldest' ? 'oldest' : 'latest',
  };
  if (search) params.keyword = search;
  if (filters.gender !== '전체') params.gender = filters.gender === '남성' ? 'MALE' : 'FEMALE';
  if (filters.ageMin !== '') params.minAge = filters.ageMin;
  if (filters.ageMax !== '') params.maxAge = filters.ageMax;
  if (filters.heightMin !== '') params.minHeight = filters.heightMin;
  if (filters.heightMax !== '') params.maxHeight = filters.heightMax;
  if (filters.weightMin !== '') params.minWeight = filters.weightMin;
  if (filters.weightMax !== '') params.maxWeight = filters.weightMax;
  if (filters.tags.length > 0) params.skills = filters.tags.join(',');
  return params;
}

export default function TalentsPage() {
  const navigate = useNavigate();
  const { isLoggedIn, role, isTalent, isProducer, switchRole, accountLoaded } = useAuth();
  const { talentMap, toggleSave, boardPickerState, savedConfirmState, selectBoard, createAndSelectBoard, closeBoardPicker, closeSavedConfirm } = useSavedItems();
  const { available: aiSearchAvailable, checking: aiSearchChecking } = useAiSearchAvailability();
  const isRestoringFromDetail = sessionStorage.getItem('talents_from') === 'detail';
  const { ad: talentsPageAd, loading: adLoading } = useRotatingAd('talents-grid');
  const adShown = Boolean(talentsPageAd);

  // AI 검색 모드 — 주소의 ?ai=1이 기준. 쓸 수 있는 제작사에게만 켜진다 (배우·일반 계정은 늘 키워드 검색).
  const [searchParams, setSearchParams] = useSearchParams();
  const aiRequested = searchParams.get('ai') === '1';
  const canUseAi = role === 'PRODUCER' && aiSearchAvailable;
  const aiMode = canUseAi && aiRequested;
  // ?ai=1인데 세션·AI 권한 확인이 아직이면 어느 모드인지 모른다. 그동안은 키워드 목록을
  // 불러오지도, 스크롤을 되돌리지도, 키워드 화면을 그리지도 않는다 (AI 결과로 돌아오는 중일 수 있다).
  const aiPending = aiRequested && (!accountLoaded || (role === 'PRODUCER' && aiSearchChecking));
  // AI 검색어·결과는 'AI 모드에서 연 상세'에서 돌아올 때만 되살린다. 키워드 모드에서 연
  // 상세는 AI 기록을 지우고 가므로(아래 onDetail), 예전 AI 결과가 다시 나타나지 않는다.
  const restoreAi = isRestoringFromDetail && aiRequested;
  const [aiInput, setAiInput] = useState(() => {
    if (restoreAi) return sessionStorage.getItem('talents_ai_query') ?? '';
    sessionStorage.removeItem('talents_ai_query');
    return '';
  });
  const [aiResult, setAiResult] = useState(() => {
    if (restoreAi) return readJson('talents_ai_result');
    sessionStorage.removeItem('talents_ai_result');
    return null;
  });
  const [aiSearching, setAiSearching] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [aiPage, setAiPage] = useState(() => {
    if (restoreAi) return Number(sessionStorage.getItem('talents_ai_page')) || 1;
    sessionStorage.removeItem('talents_ai_page');
    return 1;
  });

  const [search, setSearch] = useState(() => {
    if (isRestoringFromDetail) return sessionStorage.getItem('talents_search') ?? '';
    sessionStorage.removeItem('talents_search');
    return '';
  });
  const [searchInput, setSearchInput] = useState(() => {
    if (isRestoringFromDetail) return sessionStorage.getItem('talents_search') ?? '';
    return '';
  });
  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(() => {
    if (isRestoringFromDetail) {
      try {
        const saved = sessionStorage.getItem('talents_filters');
        return saved ? JSON.parse(saved) : EMPTY_FILTERS;
      } catch { return EMPTY_FILTERS; }
    }
    sessionStorage.removeItem('talents_filters');
    return EMPTY_FILTERS;
  });
  const [sort, setSort] = useState(() => {
    if (isRestoringFromDetail) return sessionStorage.getItem('talents_sort') ?? 'latest';
    sessionStorage.removeItem('talents_sort');
    return 'latest';
  });
  // 사진 없는 배우를 숨길지. 기본은 꺼짐 — 처음 온 사람에게는 등록된 배우를 모두 보여준다.
  // 켠 뒤에는 상세를 다녀와도 그 상태가 유지되게 세션에 남긴다.
  const [photoOnly, setPhotoOnly] = useState(
    () => sessionStorage.getItem('talents_photo_only') === 'on',
  );
  const searchInputRef = useRef(null);
  const searchRowRef = useRef(null);
  const searchSlotRef = useRef(null);
  const plainBtnRef = useRef(null);
  const filterBtnRef = useRef(null);
  // 화면에 들어올 때 한 번만 고른다. 입력 중에 문구가 바뀌면 읽던 사람이 놓친다.
  const [aiPlaceholder] = useState(() => pickAiPlaceholder());
  // 처음 들어오면 이 버튼이 무엇인지 3초 동안 저절로 알려준다 — 누르기 전에는 알 길이 없다
  const [tipShown, setTipShown] = useState(false);
  const [talents, setTalents] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [page, setPage] = useState(() => {
    if (isRestoringFromDetail) {
      const saved = sessionStorage.getItem('talents_page');
      return saved ? parseInt(saved, 10) : 1;
    }
    sessionStorage.removeItem('talents_page');
    sessionStorage.removeItem('talents_scrollY');
    return 1;
  });
  const [showActorOnlyModal, setShowActorOnlyModal] = useState(false);
  const [showProducerOnlyModal, setShowProducerOnlyModal] = useState(false);

  /**
   * 한 페이지에 보여줄 배우를 가져온다.
   *
   * 광고가 1페이지의 한 칸을 차지하므로 그 페이지는 배우가 14명이고, 밀려난 한 명부터
   * 2페이지가 시작된다. 서버는 offset이 아니라 페이지 단위로만 주기 때문에, 필요한 구간이
   * 서버 페이지 두 개에 걸치면 둘을 받아 이어 붙인 뒤 잘라 쓴다.
   * (예전에는 1페이지에 광고를 16번째 칸으로 얹어, 칸 수가 페이지마다 달라 보였다)
   */
  /**
   * 사진 있는 배우만 볼 때 쓰는 보관함.
   *
   * 서버는 사진 유무를 모른 채 15명씩 주므로, 15칸을 채우려면 모자란 만큼 다음
   * 서버 페이지를 더 받아와야 한다. 페이지를 옮길 때마다 처음부터 다시 받지 않도록
   * 받아온 사람을 순서대로 쌓아둔다. 검색어·필터·정렬이 바뀌면 비운다.
   */
  const keptCacheRef = useRef({ key: '', items: [], serverPages: 0, total: 0, exhausted: false, pending: null });

  // 늦게 도착한 예전 응답이 최신 화면을 덮어쓰지 않게 요청마다 번호를 매긴다
  const fetchIdRef = useRef(0);

  const fetchTalents = useCallback(async (targetPage, currentSearch, currentFilters, currentSort, adOccupiesSlot, onlyWithPhoto) => {
    const myFetch = ++fetchIdRef.current;
    const isStale = () => myFetch !== fetchIdRef.current;

    setIsLoading(true);
    setIsError(false);
    try {
      const need = talentsOnPage(targetPage, adOccupiesSlot);
      const start = talentOffset(targetPage, adOccupiesSlot);

      if (!onlyWithPhoto) {
        const serverPage = Math.floor(start / PAGE_SIZE) + 1;
        const within = start % PAGE_SIZE;

        const res = await getTalentList(buildParams(serverPage, currentSearch, currentFilters, currentSort));
        let list = listOf(res);
        const total = res?.total ?? res?.totalElements ?? list.length;

        // 구간이 다음 서버 페이지까지 넘어가면 이어 받는다. 덜 온 페이지는 마지막 페이지다.
        if (within + need > list.length && list.length === PAGE_SIZE) {
          const next = await getTalentList(buildParams(serverPage + 1, currentSearch, currentFilters, currentSort));
          list = list.concat(listOf(next));
        }

        if (isStale()) return;
        setTalents(list.slice(within, within + need).map(mapTalent));
        setTotalPages(pageCountOf(total, adOccupiesSlot));
        return;
      }

      // 사진 있는 배우만 — 칸이 찰 때까지 서버 페이지를 더 받는다
      const key = JSON.stringify([currentSearch, currentFilters, currentSort]);
      if (keptCacheRef.current.key !== key) {
        keptCacheRef.current = { key, items: [], serverPages: 0, total: 0, exhausted: false };
      }
      const cache = keptCacheRef.current;
      const keptOf = (items) => withPhotoOnly(items.map(mapTalent), true);

      /**
       * 다음 서버 페이지를 한 장 더 받는다.
       *
       * 페이지를 빠르게 넘기면 이 함수가 여러 번 겹쳐 불린다. 받는 중인 요청이
       * 있으면 그것을 함께 기다린다 — 각자 받으면 같은 페이지를 두 번 받아
       * 보관함에 같은 사람이 두 번 들어간다.
       */
      const loadNextPage = () => {
        if (!cache.pending) {
          const next = cache.serverPages + 1;
          cache.pending = getTalentList(buildParams(next, currentSearch, currentFilters, currentSort))
            .then(res => {
              const list = listOf(res);
              cache.total = res?.total ?? res?.totalElements ?? cache.total;
              cache.items = cache.items.concat(list);
              cache.serverPages = next;
              if (list.length < PAGE_SIZE) cache.exhausted = true;   // 더 받을 것이 없다
            })
            .finally(() => { cache.pending = null; });
        }
        return cache.pending;
      };

      while (!cache.exhausted && keptOf(cache.items).length < start + need) {
        await loadNextPage();
        if (isStale()) return;
      }

      const kept = keptOf(cache.items);
      const keptTotal = estimateKeptTotal(cache.items.length, kept.length, cache.total, cache.exhausted);

      if (isStale()) return;
      setTalents(kept.slice(start, start + need));
      setTotalPages(Math.max(targetPage, pageCountOf(keptTotal, adOccupiesSlot)));
    } catch {
      if (!isStale()) setIsError(true);
    } finally {
      if (!isStale()) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // 광고 자리가 정해지기 전에 부르면 한 페이지에 몇 명을 실을지 모른 채 요청하게 된다
    if (adLoading) return;
    fetchTalents(page, search, appliedFilters, sort, adShown, photoOnly);
  }, [page, search, appliedFilters, sort, adShown, adLoading, photoOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    sessionStorage.removeItem('talents_from');
  }, []);

  useEffect(() => {
    if (aiPending) return;
    const savedY = sessionStorage.getItem('talents_scrollY');
    // AI 모드는 저장해 둔 결과를 바로 그리므로 목록 로딩을 기다리지 않는다
    if (savedY && (aiMode || !isLoading)) {
      window.scrollTo(0, parseInt(savedY, 10));
      sessionStorage.removeItem('talents_scrollY');
    }
  }, [isLoading, aiMode, aiPending]);

  // 바뀌지 않는 함수로 둔다 — 필터 적용처럼 memo된 곳에서도 부른다
  const clearAiSearch = useCallback(() => {
    setAiInput('');
    setAiResult(null);
    setAiError(null);
    setAiPage(1);
    sessionStorage.removeItem('talents_ai_query');
    sessionStorage.removeItem('talents_ai_result');
    sessionStorage.removeItem('talents_ai_page');
  }, []);

  // seed: 켜는 순간 검색창에 적혀 있던 글. 검색창이 하나뿐이라 켠다고 글이 사라지면 지운 것처럼 보인다.
  const setAiMode = (on, seed = '') => {
    // 키워드 모드에서 새로 켜면 빈 상태에서 시작한다 — 전에 켰을 때의 결과는 지금 검색과 무관하다
    if (on) {
      clearAiSearch();
      if (seed.trim()) setAiInput(seed.slice(0, AI_SEARCH_MAX_QUERY));
    }
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (on) next.set('ai', '1');
      else next.delete('ai');
      return next;
    }, { replace: true });
  };

  const runAiSearch = async (text) => {
    const q = text.trim();
    if (!q || aiSearching) return;
    setAiSearching(true);
    setAiError(null);
    setAiResult(null);
    setAiPage(1);
    sessionStorage.removeItem('talents_ai_result');
    sessionStorage.removeItem('talents_ai_page');
    try {
      const res = await searchTalentsByAi({ query: q, limit: 20 });
      setAiResult(res);
      sessionStorage.setItem('talents_ai_query', q);
      try { sessionStorage.setItem('talents_ai_result', JSON.stringify(res)); } catch {}
    } catch (err) {
      setAiError(describeAiSearchError(err));
    } finally {
      setAiSearching(false);
    }
  };

  const getFilteredCount = useCallback(async (tempFilters, tempSort) => {
    try {
      const params = buildParams(1, search, tempFilters, tempSort || sort);
      const res = await getTalentList({ ...params, perpage: 1 });
      return res?.total ?? res?.totalElements ?? 0;
    } catch {
      return 0;
    }
  }, [search, sort]);

  const handleApplyFilters = useCallback((newFilters, newSort) => {
    clearAiSearch();
    setAppliedFilters(newFilters);
    if (newSort) setSort(newSort);
    setPage(1);
    sessionStorage.setItem('talents_page', '1');
    sessionStorage.setItem('talents_filters', JSON.stringify(newFilters));
    if (newSort) sessionStorage.setItem('talents_sort', newSort);
  }, [clearAiSearch]);

  const handleReset = useCallback(() => {
    setAppliedFilters(EMPTY_FILTERS);
    setSort('latest');
    setPage(1);
    sessionStorage.setItem('talents_filters', JSON.stringify(EMPTY_FILTERS));
    sessionStorage.setItem('talents_sort', 'latest');
    sessionStorage.setItem('talents_page', '1');
  }, []);

  const handleSave = (id) => {
    if (role !== 'PRODUCER') {
      setShowProducerOnlyModal(true);
      return;
    }
    toggleSave(id, 'TALENT_PROFILE');
  };

  /**
   * AI 결과는 '조건을 하나 더 건 목록'으로 다룬다.
   *
   * 같은 그리드, 같은 사진 토글, 같은 페이지 나눔을 쓴다. 그래서 조건을 풀면
   * 보고 있던 목록이 그대로 다시 나타난다 — 다른 화면으로 떠나지 않는다.
   * 다만 AI는 한 번에 20명까지만 골라오므로, 가려진 사람을 더 채워오지는 못한다.
   */
  const aiFound = (aiResult?.talents ?? []).map(mapTalent).filter(item => item.id != null);
  const aiTalents = withPhotoOnly(aiFound, photoOnly);
  const showingAi = Boolean(aiResult) && !aiSearching && !aiError;
  const aiTotalPages = aiPageCount(aiTalents);
  // 인상·분위기는 걸러내는 조건이 아니라 사진 순서를 정할 때 쓴 해석이라 맨 뒤에 붙인다
  const aiConditionChips = showingAi
    ? [
      ...conditionRows(aiResult.conditions),
      ...(aiResult.appearance?.trim() ? [['인상·분위기', aiResult.appearance.trim()]] : []),
    ]
    : [];

  const currentPage = showingAi ? Math.min(aiPage, aiTotalPages) : page;
  const pageCount = showingAi ? aiTotalPages : totalPages;
  // 광고는 전체 목록 1페이지에만 — AI 결과는 물어본 것에 대한 답이라 끼우지 않는다
  const showAdOnThisPage = !showingAi && currentPage === 1 && adShown;
  // AI 결과를 보고 있으면 목록 쪽 로딩·실패는 화면에 끌어올리지 않는다
  const busy = aiSearching || (!showingAi && isLoading);
  const listError = isError && !showingAi;
  // 사진 걸러내기는 목록이면 받아올 때 끝나고(여기서 또 거르면 페이지가 빈다),
  // AI 결과는 받아둔 20명 안에서 거른다.
  const pageItems = showingAi ? aiPageOf(aiTalents, currentPage) : talents;

  /**
   * 검색창 폭을 서서히 바꾼다.
   *
   * max-width가 none에서 픽셀로 바뀌면 브라우저는 애니메이션을 걸지 않는다.
   * 그래서 지금 폭을 픽셀로 한 번 못박고(전환 끔), 레이아웃을 강제로 한 번 계산시킨 뒤
   * 목표 폭을 준다. 그래야 양 끝이 같이 줄어드는 움직임이 보인다.
   *
   * 돌아올 때의 목표는 '꽉 찬 줄'이 아니라 '필터를 뺀 나머지'다. 줄 전체 폭을 주면
   * 제자리보다 넓게 늘어난 뒤 max-width를 거둘 때 덜컥 줄어든다 — 그게 어색함의 원인이었다.
   */
  useEffect(() => {
    const slot = searchSlotRef.current;
    const row = searchRowRef.current;
    if (!slot || !row) return undefined;

    const GAP = 12;
    const widthOf = (ref, fallback = 0) => ref.current?.offsetWidth || fallback;

    /** AI 모드에서 검색창이 차지할 폭. 양옆은 '일반 검색'과 '필터'가 쓴다. */
    const aiWidth = () => {
      // 막 켜진 순간에는 '일반 검색'이 아직 0폭이라 자리 잡을 폭을 대신 쓴다
      const sides = widthOf(filterBtnRef) + widthOf(plainBtnRef, 130) + GAP * 3;
      // 너무 좁으면 안내 문구가 잘리고, 너무 넓으면 줄어든 느낌이 안 난다
      return Math.max(320, Math.min(560, row.clientWidth - sides));
    };

    /** 평소 폭 — 필터 자리를 뺀 나머지. 제자리와 같아야 끝에서 튀지 않는다. */
    const plainWidth = () => row.clientWidth - widthOf(filterBtnRef) - GAP;

    // 지금 폭을 픽셀로 한 번 못박고 레이아웃을 계산시킨 뒤 목표 폭을 준다.
    // 이 과정을 건너뛰면 none → 560px이라 브라우저가 중간 값을 그리지 않는다.
    const current = slot.offsetWidth;
    slot.style.transition = 'none';
    slot.style.maxWidth = `${current}px`;
    void slot.offsetWidth;
    slot.style.transition = '';

    let restore;
    if (aiMode) {
      slot.style.maxWidth = `${aiWidth()}px`;
    } else {
      slot.style.maxWidth = `${plainWidth()}px`;
      // 다 늘어난 뒤에는 레이아웃에 맡긴다. 폭이 이미 제자리라 거둬도 움직이지 않는다.
      restore = setTimeout(() => {
        if (searchSlotRef.current) searchSlotRef.current.style.maxWidth = '';
      }, 800);
    }

    const onResize = () => {
      if (!searchSlotRef.current) return;
      searchSlotRef.current.style.maxWidth = aiMode ? `${aiWidth()}px` : '';
    };
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('resize', onResize);
      if (restore) clearTimeout(restore);
    };
  }, [aiMode]);

  const greeted = useRef(false);
  useEffect(() => {
    if (greeted.current || !canUseAi || aiMode) return undefined;
    greeted.current = true;
    setTipShown(true);
    const hide = setTimeout(() => setTipShown(false), 3000);
    return () => clearTimeout(hide);
  }, [canUseAi, aiMode]);

  const handlePageChange = (nextPage) => {
    if (showingAi) {
      setAiPage(nextPage);
      sessionStorage.setItem('talents_ai_page', String(nextPage));
      window.scrollTo(0, 0);
      return;
    }
    setPage(nextPage);
    sessionStorage.setItem('talents_page', String(nextPage));
    window.scrollTo(0, 0);
  };

  const activeFilterCount = [
    appliedFilters.gender !== '전체',
    appliedFilters.ageMin !== '',
    appliedFilters.ageMax !== '',
    appliedFilters.heightMin !== '',
    appliedFilters.heightMax !== '',
    appliedFilters.weightMin !== '',
    appliedFilters.weightMax !== '',
    appliedFilters.tags.length > 0,
  ].filter(Boolean).length;

  const handlePortfolioCreateClick = () => {
    if (role === 'TALENT') {
      navigate('/talent/profile/edit');
      return;
    }
    setShowActorOnlyModal(true);
  };

  return (
    <PageLayout>
      {showActorOnlyModal && (
        <RoleOnlyModal
          title="배우 전용 기능입니다"
          message={isLoggedIn ? '포트폴리오 만들기는 배우 계정에서만 이용할 수 있습니다.' : '배우로 로그인해서 이용해주세요.'}
          onClose={() => setShowActorOnlyModal(false)}
          hasBothRoles={isLoggedIn && isProducer && isTalent}
          onSwitch={() => { switchRole('TALENT'); window.location.href = '/talent/dashboard'; }}
          switchLabel="배우로 계정 전환하기"
          onboardingPath={isLoggedIn && !isTalent ? '/onboarding/talent' : undefined}
          onboardingLabel="배우 계정 만들러 가기"
        />
      )}
      {showProducerOnlyModal && (
        <RoleOnlyModal
          title="제작사 전용 기능입니다"
          message={isLoggedIn ? '배우 저장은 제작사 계정에서만 이용할 수 있습니다.' : '제작사로 로그인해서 이용해주세요.'}
          onClose={() => setShowProducerOnlyModal(false)}
          hasBothRoles={isLoggedIn && isTalent && isProducer}
          onSwitch={() => { switchRole('PRODUCER'); window.location.href = '/producer/dashboard'; }}
          switchLabel="제작사로 계정 전환하기"
          onboardingPath={isLoggedIn && !isProducer ? '/onboarding/producer' : undefined}
          onboardingLabel="제작사 계정 만들러 가기"
        />
      )}
      {boardPickerState && (
        <BoardPickerModal
          boards={boardPickerState.options}
          targetType={boardPickerState.targetType}
          onClose={closeBoardPicker}
          onSelect={selectBoard}
          onCreateAndSelect={createAndSelectBoard}
        />
      )}
      {savedConfirmState && (
        <SavedConfirmModal
          boardName={savedConfirmState.boardName}
          onClose={closeSavedConfirm}
          onNavigateToArchive={() => {
            try { localStorage.setItem('producer-bookmark-open-board', String(savedConfirmState.boardId)); } catch {}
            closeSavedConfirm();
            navigate('/producer/archive');
          }}
        />
      )}
      {filterOpen && (
        <FilterSidebar filters={appliedFilters} sort={sort} onApply={handleApplyFilters} onReset={handleReset} onClose={() => setFilterOpen(false)} getFilteredCount={getFilteredCount} />
      )}

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '60px 40px 80px', minHeight: 'calc(100vh + 40px)', fontFamily: ff }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '32px' }}>
          <div>
            <h1 style={{ ...t('40px', 700, C.bk), margin: '0 0 8px 0' }}>배우 찾기</h1>
            <span style={t('16px', 400, C.sub)}>검증된 배우들을 검색하고 직접 캐스팅 제안을 보내보세요.</span>
          </div>
          <button onClick={handlePortfolioCreateClick} className={styles.portfolioBtn}>
            <span className={styles.portfolioBtnPlus}>+</span>
            포트폴리오 만들기
          </button>
        </div>

        {/* Search & Filter */}
        {/* 가운데 정렬이라 AI 모드에서 검색창이 줄 때 양쪽에서 함께 좁아진다 */}
        <div ref={searchRowRef} style={{ display: 'flex', gap: '12px', marginBottom: '32px', alignItems: 'center', justifyContent: 'center' }}>
          <div ref={searchSlotRef} className={styles.searchSlot}>
            <div className={`${styles.searchBar}${aiMode ? ` ${styles.ai}` : ''}`}>
              <svg className={styles.searchIcon} width="18" height="18" viewBox="0 0 24 24" fill="none">
                <circle cx="11" cy="11" r="8" stroke={C.g300} strokeWidth="1.8" />
                <path d="M21 21l-4.35-4.35" stroke={C.g300} strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              <input
                ref={searchInputRef}
                value={aiMode ? aiInput : searchInput}
                onChange={e => (aiMode ? setAiInput(e.target.value.slice(0, AI_SEARCH_MAX_QUERY)) : setSearchInput(e.target.value))}
                onKeyDown={e => {
                  if (aiMode) {
                    // 한글 조합 중 Enter는 두 번 들어온다 — AI 검색은 비싸서 한 번만 보낸다
                    if (e.key === 'Enter' && !e.nativeEvent.isComposing) runAiSearch(aiInput);
                    return;
                  }
                  if (e.key !== 'Enter') return;
                  setSearch(searchInput);
                  setPage(1);
                  sessionStorage.setItem('talents_page', '1');
                  sessionStorage.setItem('talents_search', searchInput);
                }}
                placeholder={aiMode ? aiPlaceholder : '이름, 특기, 분야로 검색 (Enter)'}
                aria-label={aiMode ? 'AI 검색어' : '배우 검색어'}
                style={{ fontFamily: ff }}
              />

              {/* AI 검색은 쓸 수 있는 계정에서만 보인다 */}
              {canUseAi && (
                <button
                  type="button"
                  className={styles.aiOrb}
                  aria-pressed={aiMode}
                  aria-label={aiMode ? 'AI로 검색하기' : '캐스타의 새로운 검색 기능 사용해보기'}
                  onClick={() => {
                    if (aiMode) { runAiSearch(aiInput); return; }
                    setAiMode(true, searchInput);
                    searchInputRef.current?.focus();
                  }}
                >
                  <AiOrbMark />
                  <span className={styles.aiOrbSend} aria-hidden="true" />
                  <span className={`${styles.aiTip}${tipShown && !aiMode ? ` ${styles.shown}` : ''}`}>
                    캐스타의 새로운 검색 기능 사용해보기
                  </span>
                </button>
              )}
            </div>
          </div>

          <button
            type="button"
            ref={plainBtnRef}
            className={`${styles.plainSearchBtn}${aiMode ? ` ${styles.shown}` : ''}`}
            tabIndex={aiMode ? 0 : -1}
            onClick={() => setAiMode(false)}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
              <path d="m20 20-3.5-3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            일반 검색
          </button>

          <button
            ref={filterBtnRef}
            onClick={() => setFilterOpen(true)}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 20px', height: '50px', background: C.white, border: `1px solid ${activeFilterCount > 0 ? C.bk : C.g200}`, borderRadius: '12px', cursor: 'pointer', ...t('15px', 500, C.bk), whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" stroke={C.bk} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
            필터{activeFilterCount > 0 && <span style={{ padding: '2px 7px', borderRadius: '100px', background: C.bk, ...t('12px', 700, C.white) }}>{activeFilterCount}</span>}
          </button>
        </div>

        {/* AI 검색이 실패해도 목록은 덮지 않는다 — 보고 있던 배우를 계속 볼 수 있게 위에만 알린다 */}
        {aiError && !aiSearching && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 14px', marginBottom: '16px', background: '#FFF8F8', border: '1px solid #F0CDCD', borderRadius: '12px' }}>
            <div style={{ flex: 1 }}>
              <div style={t('14px', 600, C.bk)}>{aiError.title}</div>
              <div style={{ ...t('13px', 400, C.sub), marginTop: '4px', lineHeight: '160%', whiteSpace: 'pre-line' }}>{aiError.description}</div>
            </div>
            {aiError.showFallback && (
              <button
                type="button"
                onClick={() => setAiMode(false)}
                style={{ flexShrink: 0, padding: '8px 14px', borderRadius: '100px', border: `1px solid ${C.g200}`, background: C.white, cursor: 'pointer', ...t('13px', 600, C.bk) }}
              >
                일반 검색으로 찾기
              </button>
            )}
            <button
              type="button"
              onClick={() => setAiError(null)}
              aria-label="안내 닫기"
              style={{ flexShrink: 0, display: 'inline-flex', padding: '4px', border: 'none', background: 'none', cursor: 'pointer', color: C.g400 }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        )}

        {/* AI가 읽어낸 조건 — 걸어 둔 필터처럼 한 줄로 보여주고, 풀면 전체 목록으로 돌아간다.
            칩마다 ✕를 두지는 않는다: AI는 조건을 다 만족하는 배우만 골라오므로 하나를 떼도
            빠진 사람이 돌아오지 않는다 — 눌러도 결과가 안 바뀌는 버튼이 된다. */}
        {showingAi && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '12px 14px', marginBottom: '16px', background: C.white, border: `1px solid ${C.g200}`, borderRadius: '12px' }}>
            <span style={t('13px', 700, C.bk)}>AI가 읽은 조건</span>
            {aiConditionChips.length === 0 ? (
              <span style={t('13px', 400, C.g400)}>걸러낼 조건을 찾지 못해, 인상 설명만으로 순서를 매겼어요</span>
            ) : aiConditionChips.map(([label, value]) => (
              <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 11px', borderRadius: '100px', background: C.bg, border: `1px solid ${C.g200}` }}>
                <span style={t('12px', 600, C.g400)}>{label}</span>
                <span style={t('13px', 600, C.bk)}>{value}</span>
              </span>
            ))}
            <span style={{ ...t('12px', 400, C.sub), marginLeft: '2px' }}>그대로 적용했어요</span>
            <button
              type="button"
              onClick={clearAiSearch}
              style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '100px', border: `1px solid ${C.g200}`, background: C.white, cursor: 'pointer', ...t('13px', 500, C.g500) }}
            >
              조건 해제
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        )}

        {/* Results meta */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
          <button
            type="button"
            role="switch"
            aria-checked={photoOnly}
            onClick={() => {
              const next = !photoOnly;
              setPhotoOnly(next);
              sessionStorage.setItem('talents_photo_only', next ? 'on' : 'off');
            }}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '6px 12px 6px 8px', borderRadius: '100px',
              border: `1px solid ${photoOnly ? C.bk : C.g200}`,
              background: C.white, cursor: 'pointer',
              ...t('14px', 500, photoOnly ? C.bk : C.sub),
            }}
          >
            <span style={{
              width: '34px', height: '20px', borderRadius: '10px', position: 'relative',
              background: photoOnly ? C.bk : C.g300, transition: 'background 0.2s', flexShrink: 0,
            }}>
              <span style={{
                position: 'absolute', top: '2px', left: photoOnly ? '16px' : '2px',
                width: '16px', height: '16px', borderRadius: '50%',
                background: C.white, transition: 'left 0.2s',
              }} />
            </span>
            사진 있는 배우만
          </button>
        </div>

        {/* Loading / Error */}
        {busy && (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'center' }}><LoadingSpinner size="lg" /></div>
            {aiSearching && (
              <div style={{ ...t('14px', 400, C.sub), marginTop: '16px' }}>공고를 읽고 조건을 맞춰보고 있어요. 몇 초 걸려요.</div>
            )}
          </div>
        )}
        {listError && (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={t('16px', 600, C.bk)}>데이터를 불러오지 못했습니다.</div>
            <button onClick={fetchTalents} style={{ marginTop: '12px', padding: '8px 20px', borderRadius: '10px', border: `1px solid ${C.g200}`, background: C.white, cursor: 'pointer', ...t('14px', 500, C.bk) }}>
              다시 시도
            </button>
          </div>
        )}

        {/* Grid */}
        {!busy && !listError && pageItems.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
            {/* 토글 때문에 비었는데 '검색 결과가 없다'고 하면 검색어를 고치게 된다 */}
            {showingAi && aiFound.length === 0 ? (
              <>
                <div style={t('18px', 600, C.bk)}>조건에 맞는 배우가 아직 없어요</div>
                <div style={{ ...t('14px', 400, C.sub), marginTop: '8px', lineHeight: '160%' }}>
                  필수 조건을 모두 만족하는 배우가 없어서, 조건을 임의로 풀지 않고 그대로 알려드려요.
                </div>
              </>
            ) : photoOnly ? (
              <>
                <div style={t('18px', 600, C.bk)}>사진이 있는 배우를 찾지 못했습니다</div>
                <div style={{ ...t('14px', 400, C.sub), marginTop: '8px' }}>
                  토글을 끄면 사진 없는 배우까지 함께 볼 수 있습니다.
                </div>
              </>
            ) : (
              <>
                <div style={t('18px', 600, C.bk)}>검색 결과가 없습니다</div>
                <div style={{ ...t('14px', 400, C.sub), marginTop: '8px' }}>다른 검색어나 필터를 사용해 보세요.</div>
              </>
            )}
          </div>
        ) : !busy && !listError && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '20px', marginBottom: '48px' }}>
            {pageItems.map((talent, idx) => {
              const savedEntry = talentMap[String(talent.id)];
              return (
                <React.Fragment key={talent.id}>
                  <TalentCard
                    talent={{ ...talent, saved: Boolean(savedEntry) }}
                    onDetail={(id) => {
                      sessionStorage.setItem('talents_scrollY', String(window.scrollY));
                      sessionStorage.setItem('talents_from', 'detail');
                      sessionStorage.setItem('talents_search', search);
                      sessionStorage.setItem('talents_filters', JSON.stringify(appliedFilters));
                      sessionStorage.setItem('talents_sort', sort);
                      // 목록에서 연 상세는 AI 기록을 지우고 간다 — 돌아와서 지난 결과가 되살아나지 않게
                      if (!showingAi) {
                        sessionStorage.removeItem('talents_ai_query');
                        sessionStorage.removeItem('talents_ai_result');
                        sessionStorage.removeItem('talents_ai_page');
                      }
                      const target = pageItems.find(item => item.id === id);
                      navigate(`/talents/${target?.slug || id}`, { state: { from: showingAi ? '/talents?ai=1' : '/talents' } });
                    }}
                    onSave={handleSave}
                  />
                  {showAdOnThisPage && idx === 3 && <AdGridCard ad={talentsPageAd} variant="talent" />}
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* Pagination - 하단 번호 + 좌우 고정 화살표 */}
        <GroupedPagination
          currentPage={currentPage}
          totalPages={pageCount}
          onPageChange={handlePageChange}
        />
      </div>
    </PageLayout>
  );
}
